import { z } from 'zod'
import type { AITool } from './types'
import { exerciseProcessor } from '../../services/exerciseProcessor'

// OpenAI compatible schema - all fields required (no optional fields)
const parametersSchema = z.object({
  limit: z.number()
    .min(1)
    .max(50)
    .describe('Number of recent workouts to retrieve (1-50)'),
  date: z.string()
    .describe('Specific date (YYYY-MM-DD) to retrieve all workouts from that day, or "recent" to get most recent workouts')
})


export const getRecentWorkouts: AITool = {
  name: 'getRecentWorkouts',
  description: 'Get recent workout sessions.',
  parameters: parametersSchema,
  
  execute: async (args, context) => {
    const { workoutData } = context
    const { limit, date } = args
    
    // Check if workout data is available
    if (!workoutData.workouts || workoutData.workouts.length === 0) {
      return {
        summary: 'No workout data available.',
        workouts: [],
        totalFound: 0,
        mode: date ? 'date' : 'recent'
      }
    }
    
    let selectedWorkouts: typeof workoutData.workouts
    let mode: 'date' | 'recent'
    
    if (date !== 'recent') {
      // Date mode: Get all workouts from specific date
      mode = 'date'
      const targetDate = new Date(date)
      const targetDateString = targetDate.toISOString().split('T')[0]
      
      selectedWorkouts = workoutData.workouts.filter(workout => {
        const workoutDate = new Date(workout.start_time)
        const workoutDateString = workoutDate.toISOString().split('T')[0]
        return workoutDateString === targetDateString
      })
    } else {
      // Recent mode: Get N most recent workouts
      mode = 'recent'
      selectedWorkouts = workoutData.workouts.slice(0, limit)
    }
    
    // Calculate aggregated metrics instead of returning raw data
    let totalSets = 0
    let totalVolume = 0
    let totalDuration = 0
    const muscleGroupVolume: Record<string, number> = {}
    const workoutTypes: Record<string, number> = {}
    
    // Process each workout for aggregation
    selectedWorkouts.forEach(workout => {
      const startTime = new Date(workout.start_time)
      const endTime = new Date(workout.end_time)
      const duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000 / 60)
      totalDuration += duration
      
      // Count workout type
      const workoutTitle = workout.title || 'Untitled Workout'
      workoutTypes[workoutTitle] = (workoutTypes[workoutTitle] || 0) + 1
      
      // Process exercises for volume and muscle groups
      workout.exercises.forEach(exercise => {
        // Get muscle groups for this exercise
        const muscleContributions = exerciseProcessor.getMuscleContributionsFromExercise(
          exercise.title,
          exercise.exercise_template_id,
          false // Only primary movers for simplicity
        )
        
        // Count sets and calculate volume
        const validSets = exercise.sets.filter(set => set.type !== 'empty')
        totalSets += validSets.length
        
        validSets.forEach(set => {
          const weight = set.weight_kg || 0
          const reps = set.reps || 0
          const setVolume = weight * reps
          totalVolume += setVolume
          
          // Distribute volume to muscle groups
          muscleContributions.forEach(mc => {
            muscleGroupVolume[mc.muscleGroup] = (muscleGroupVolume[mc.muscleGroup] || 0) + setVolume
          })
        })
      })
    })
    
    // Get date range
    const firstWorkoutDate = selectedWorkouts.length > 0 ? new Date(selectedWorkouts[selectedWorkouts.length - 1].start_time) : null
    const lastWorkoutDate = selectedWorkouts.length > 0 ? new Date(selectedWorkouts[0].start_time) : null
    
    // Generate summary based on mode
    let summary: string
    if (selectedWorkouts.length === 0) {
      summary = date 
        ? `No workouts found on ${new Date(date).toLocaleDateString()}.`
        : 'No workouts found.'
    } else if (mode === 'date') {
      summary = `Found ${selectedWorkouts.length} workout${selectedWorkouts.length === 1 ? '' : 's'} on ${new Date(date!).toLocaleDateString()}.`
    } else {
      summary = `Retrieved ${selectedWorkouts.length} most recent workout${selectedWorkouts.length === 1 ? '' : 's'} from ${firstWorkoutDate?.toLocaleDateString()} to ${lastWorkoutDate?.toLocaleDateString()}.`
    }
    
    return {
      summary,
      sessionCount: selectedWorkouts.length,
      daysCovered: firstWorkoutDate && lastWorkoutDate ? 
        Math.ceil((lastWorkoutDate.getTime() - firstWorkoutDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 : 0,
      totalSets,
      muscleGroupVolume,
      lastWorkoutDate: lastWorkoutDate?.toISOString().split('T')[0] || null,
      lastWorkoutType: selectedWorkouts.length > 0 ? (selectedWorkouts[0].title || 'Untitled Workout') : null,
      averageSessionLength: selectedWorkouts.length > 0 ? Math.round(totalDuration / selectedWorkouts.length) : 0,
      totalVolume: Math.round(totalVolume),
      workoutTypes,
      mode,
      requestedDate: date || null
    }
  }
}